import { motion } from "framer-motion";
import { Cauldron } from "../components/Cauldron";
import { Marquee } from "../components/Marquee";
import { PixelButton } from "../components/PixelButton";

export function CauldronHero({ onSecretClicks }: { onSecretClicks?: () => void }) {
  return (
    <section
      id="home"
      className="relative min-h-[100svh] pt-20 pb-12 flex flex-col"
    >
      {/* Marquee bar at the very top */}
      <div className="absolute top-14 inset-x-0 bg-witch text-bone border-y-2 border-black overflow-hidden">
        <Marquee speed={1200} className="py-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className="font-display text-[11px] uppercase tracking-widest flex items-center gap-3"
            >
              <Star /> Booking Q3 2026 — slots open
              <Star /> Independent web studio · est. 2024
              <Star /> Sites that ship · apps that scale
              <Star /> React · TypeScript · Postgres · obsessive UX
            </span>
          ))}
        </Marquee>
      </div>

      <div className="relative flex-1 mx-auto max-w-[1400px] w-full px-4 md:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center pt-16">
        {/* Copy column */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 mb-6 px-2 py-1 border-2 border-mystic bg-grimoire/70 backdrop-blur-sm max-w-full"
          >
            <span className="w-2 h-2 bg-acid animate-pulse shrink-0" />
            <span className="font-display text-[8px] sm:text-[9px] text-mystic tracking-wider sm:tracking-widest uppercase">
              {/* Short label on mobile, full version when there's room. */}
              <span className="sm:hidden">EST. MMXXIV — WEB STUDIO</span>
              <span className="hidden sm:inline">EST. MMXXIV — INDEPENDENT WEB STUDIO</span>
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-2xl sm:text-4xl md:text-6xl lg:text-7xl leading-[1.05] text-bone"
          >
            We design &amp; build{" "}
            <span className="shimmer-text">websites</span>
            <br />
            and{" "}
            <span className="text-witch drop-shadow-[0_0_24px_rgba(155,61,245,0.7)]">
              web apps
            </span>
            <span className="text-acid animate-pulse">_</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 max-w-xl font-pixel text-lg md:text-xl text-aura/85"
          >
            Hand-coded, pixel-tuned, and built to load fast. Bespoke product and marketing work
            for founders, studios, and the occasional weirdo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-10 flex flex-wrap gap-4 items-center"
          >
            <a href="#contact">
              <PixelButton variant="primary" size="lg">
                Start a project →
              </PixelButton>
            </a>
            <a href="#work">
              <PixelButton variant="ghost" size="lg">
                See live work
              </PixelButton>
            </a>
          </motion.div>
        </div>

        {/* Cauldron column — hidden on mobile (the brand stays present via the
            cauldron favicon + Nav logo; the giant hero version takes too much
            of a phone viewport without paying for it). */}
        <div className="relative hidden lg:flex justify-center items-center min-h-[500px]">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "linear-gradient(rgba(155,61,245,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(155,61,245,0.15) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
              maskImage:
                "radial-gradient(circle at center, black 30%, transparent 70%)",
            }}
          />
          <Cauldron size={420} onSecretClicks={onSecretClicks} />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
      >
        <span className="font-terminal text-xs text-mystic uppercase tracking-widest">
          scroll ▾
        </span>
        <motion.span
          animate={{ y: [0, 4, 0] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="font-display text-[10px] text-aura/60"
        >
          ▼
        </motion.span>
      </motion.div>
    </section>
  );
}

function Star() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" className="inline-block shrink-0">
      <path d="M7 0 L8.5 5.5 L14 7 L8.5 8.5 L7 14 L5.5 8.5 L0 7 L5.5 5.5 Z" fill="currentColor" />
    </svg>
  );
}
